/** Minimal bottom-right toast provider. Mounted once in the root route. */

import type { ReactNode } from "react";
import { createContext, use, useCallback, useState } from "react";

type ToastTone = "success" | "error";
type ToastItem = { readonly id: string; readonly tone: ToastTone; readonly message: string };

type ToastApi = {
  readonly success: (message: string) => void;
  readonly error: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const DISMISS_MS = 5_000;

export function ToastProvider({ children }: { readonly children: ReactNode }) {
  const [items, setItems] = useState<readonly ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = crypto.randomUUID();
      setItems((prev) => [...prev, { id, tone, message }]);
      window.setTimeout(() => dismiss(id), DISMISS_MS);
    },
    [dismiss],
  );

  const api: ToastApi = {
    success: (message) => push("success", message),
    error: (message) => push("error", message),
  };

  return (
    <ToastContext value={api}>
      {children}
      <div className="fixed inset-x-3 bottom-[76px] z-50 flex flex-col gap-2 md:inset-x-auto md:bottom-4 md:right-4">
        {items.map((item) => (
          <div
            key={item.id}
            role="status"
            className="flex w-full items-start gap-2 rounded-md bg-surface px-4 py-3 text-sm text-fg-1 shadow-md md:w-auto md:max-w-sm"
            style={{
              borderLeft: `3px solid ${item.tone === "success" ? "var(--color-sell)" : "var(--color-danger)"}`,
              animation: "dr-in 180ms ease-out",
            }}
          >
            <span className="flex-1">{item.message}</span>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="text-fg-4 hover:text-fg-1"
              aria-label="Zamknij"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext>
  );
}

export function useToast(): ToastApi {
  const ctx = use(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
